import React, {
    useState, useEffect, useRef, useMemo
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { breakpoints, space } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import { Link } from 'components/ui';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import FilterGroup from 'components/ProductPage/ShadeFilter/FilterGroup';
import skuUtils from 'utils/Sku';
import swatchUtils from 'utils/Swatch';
import { DebouncedResize } from 'constants/events';
import skuHelpers from 'utils/skuHelpers';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR);

const SwatchListDropdown = ({ product, isCustomSet = false, onSelectedSkuChanged }) => {
    const { onSaleChildSkus = [], regularChildSkus = [] } = product;
    // Preserve referential stability of the concat'd skus array so downstream
    // FilterGroup (and any shallow-compare children) do not re-render on every
    // parent render. The class stored skus in state and only regenerated on
    // productId change via getDerivedStateFromProps.
    const skus = useMemo(() => regularChildSkus.concat(onSaleChildSkus), [regularChildSkus, onSaleChildSkus]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSmallView, setIsSmallView] = useState(null);
    const dropdownRef = useRef(null);
    const scrollRef = useRef(null);
    const pendingScrollItemRef = useRef(null);

    useEffect(() => {
        const updateSmallView = () => setIsSmallView(window.matchMedia(breakpoints.xsMax).matches);
        updateSmallView();
        window.addEventListener(DebouncedResize, updateSmallView);

        return () => window.removeEventListener(DebouncedResize, updateSmallView);
    }, []);

    const colorMatchSku = skuHelpers.getColorIQMatchSku(product.regularChildSkus);
    const showColorMatch = skuUtils.showColorIQOnPPage(product) ? colorMatchSku : false;
    const colorMatchSkuId = showColorMatch && showColorMatch.skuId;
    const selectedFilters = product.currentSku ? [product.currentSku] : [];
    const isModalOpened = isSmallView && isExpanded;
    const arrowDirection = isExpanded ? 'up' : 'down';

    const onFilterChanged = selectedSkus => {
        if (!(selectedSkus?.length && selectedSkus[selectedSkus.length - 1])) {
            return;
        }

        dropdownRef.current?.triggerDropdown(new Event('triggerDropdown'), false);
        setIsExpanded(false);
        const { skuId } = selectedSkus[selectedSkus.length - 1];
        const selectedSku = skus.find(sku => sku.skuId === skuId);

        if (onSelectedSkuChanged) {
            onSelectedSkuChanged(selectedSku);
        }

        if (!isCustomSet && selectedSku) {
            swatchUtils.handleSkuOnClick(selectedSku);
        }
    };

    const toggleMenu = (expanded, selectedItem) => {
        if (expanded) {
            pdpAnalytics.trackLinkClick({
                pageName: 'product:swatch selector modal:n/a*',
                actionInfo: 'view as list',
                linkName: 'D=c55'
            });

            // Store the target so the useEffect below can perform the scroll AFTER
            // React commits the isExpanded=true render — replaces the class's
            // setState({isExpanded}, () => scroll) callback which fired post-commit.
            pendingScrollItemRef.current = selectedItem || null;
        } else {
            pendingScrollItemRef.current = null;
        }

        setIsExpanded(expanded);
    };

    useEffect(() => {
        if (isExpanded && pendingScrollItemRef.current) {
            const selectedItem = pendingScrollItemRef.current;
            pendingScrollItemRef.current = null;
            const x = 0;
            const y = selectedItem.current?.offsetTop - space[2] || 0;
            scrollRef.current?.scroll(x, y);
        }
    }, [isExpanded]);

    return (
        <FilterGroup
            triggerDataAtLarge={Sephora.debug.dataAt('view_as_list_btn_large')}
            triggerDataAtSmall={Sephora.debug.dataAt('view_as_list_btn_small')}
            listDataAtLarge={Sephora.debug.dataAt('view_as_list_large')}
            listDataAtSmall={Sephora.debug.dataAt('view_as_list_small')}
            ref={dropdownRef}
            id='swatch_list'
            scrollRef={scrollRef}
            isSmallView={isSmallView}
            skus={skus}
            dropdownAlign='right'
            itemType={isCustomSet ? swatchUtils.SWATCH_TYPES.CIRCLE : product.swatchType}
            colorMatchSkuId={colorMatchSkuId}
            selectedFilters={selectedFilters}
            updateFilters={onFilterChanged}
            isModalOpened={isModalOpened}
            showModal={(expanded, selectedItem) => toggleMenu(expanded, selectedItem)}
            toggleDropdownOpen={(_event, expanded, selectedItem) => toggleMenu(expanded, selectedItem)}
        >
            <Link
                arrowDirection={arrowDirection}
                padding={2}
                margin={-2}
                fontSize='sm'
            >
                {getText('viewAsList')}
            </Link>
        </FilterGroup>
    );
};

export default wrapFunctionalComponent(SwatchListDropdown, 'SwatchListDropdown');
